<template>
  <Modal
    :show="show"
    size="md"
    dialogMaxWidth="640px"
    :closeOnBackdrop="false"
    :blurBackdrop="false"
    :backdropOpacity="0.5"
    :zIndex="10060"
    :show-close-button="false"
    @close="$emit('close')"
  >
    <template #header>
      <div class="whatsapp-header__text">
        <div class="whatsapp-header__icon-title">
          <div class="whatsapp-header__icon-container">
            <Icon icon="mdi:whatsapp" class="whatsapp-header__icon" />
          </div>
          <div>
            <h2 class="whatsapp-header__title">Start New WhatsApp Chat</h2>
            <p class="whatsapp-header__subtitle">
              Create a new conversation with one or more recipients
            </p>
          </div>
        </div>
      </div>
      <button
        type="button"
        class="whatsapp-header__close"
        @click="$emit('close')"
        aria-label="Close"
      >
        <Icon icon="mdi:close" width="18" height="18" />
      </button>
    </template>
    <template #body>
      <form id="whatsapp-chat-form" @submit.prevent="handleSubmit" class="whatsapp-form">
        <div v-if="!props.hideAccountSelection" class="whatsapp-field">
          <Select
            v-model="formData.account_id"
            label="Account"
            placeholder="Select an account"
            :options="accountOptions"
            :error="errors.account_id"
            required
          />
        </div>

        <div
          v-if="
            props.autoSetChannelType === 'whatsapp' &&
            props.contactPhoneNumbers &&
            props.contactPhoneNumbers.length > 0
          "
          class="whatsapp-field"
        >
          <Select
            v-model="selectedPhoneNumber"
            label="Select Phone Number"
            placeholder="Choose a phone number"
            :options="phoneNumberOptions"
            @update:model-value="handlePhoneNumberSelect"
          />
        </div>

        <div class="whatsapp-field">
          <Input
            v-model="attendeesInput"
            label="Recipients"
            :placeholder="recipientsPlaceholder"
            :error="errors.attendees_ids"
            :helper-text="recipientsHelperText"
            required
            @input="handleAttendeesInput"
          />
          <div v-if="attendeeTags.length > 0" class="whatsapp-attendee-tags">
            <div v-for="(tag, index) in attendeeTags" :key="index" class="whatsapp-attendee-tag">
              <span class="whatsapp-attendee-tag-text">{{ tag }}</span>
              <button
                type="button"
                class="whatsapp-attendee-tag-remove"
                @click="removeAttendeeTag(index)"
              >
                <Icon icon="mdi:close" />
              </button>
            </div>
          </div>
        </div>

        <div class="whatsapp-field">
          <label class="whatsapp-label">
            Message
            <span class="whatsapp-optional">(Optional)</span>
          </label>
          <textarea
            v-model="formData.text"
            class="whatsapp-textarea"
            placeholder="Enter your message here..."
            rows="6"
          ></textarea>
          <div class="whatsapp-ai-row">
            <button
              ref="aiComposeButtonRef"
              type="button"
              class="whatsapp-ai-btn whatsapp-ai-btn--compose"
              :disabled="isGeneratingAiCompose"
              :title="isGeneratingAiCompose ? 'Generating...' : 'AI Compose'"
              @click="toggleAiComposePanel"
            >
              <Icon icon="mdi:sparkles" class="whatsapp-ai-btn-icon" />
              <span>{{ isGeneratingAiCompose ? "Generating..." : "AI Compose" }}</span>
            </button>
            <button
              ref="aiRefineButtonRef"
              type="button"
              class="whatsapp-ai-btn"
              :disabled="isGeneratingAiCustom"
              :title="isGeneratingAiCustom ? 'Generating...' : 'AI Refine'"
              @click="toggleAiRefinePanel"
            >
              <Icon icon="mdi:sparkles" class="whatsapp-ai-btn-icon" />
              <span>AI Refine</span>
            </button>
          </div>

          <AiRefinePopover
            :show="showAiPanel"
            :trigger-el="aiRefineButtonRef"
            :disabled="isGeneratingAiCustom"
            @generate="handleAiCustomizationGenerate"
            @close="showAiPanel = false"
          />

          <AiRefinePopover
            :show="showAiComposePanel"
            :trigger-el="aiComposeButtonRef"
            :disabled="isGeneratingAiCompose"
            title="AI Compose"
            generate-button-label="Generate Message"
            :show-attachments="false"
            @generate="handleAiComposeGenerate"
            @close="showAiComposePanel = false"
          />
        </div>
      </form>
    </template>
    <template #footer>
      <Button variant="ghost" size="md" @click="$emit('close')" :disabled="isLoading">
        Cancel
      </Button>
      <Button
        type="submit"
        form="whatsapp-chat-form"
        variant="primary"
        size="md"
        icon="mdi:send"
        icon-position="left"
        :loading="isLoading"
        :disabled="!canSubmit"
      >
        Create Chat
      </Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { Button, Input, Modal, Select } from "@/components/ui";
import type { Channel } from "@/dtos/Unipile.dto";
import type { CreateChatRequest } from "@/dtos/Unipile.dto";
import type { SelectOption } from "@/components/ui/types";
import { Icon } from "@iconify/vue";
import AiRefinePopover from "@/components/socials/AiRefinePopover.vue";
import { computed, ref, watch } from "vue";
import AIGenerationService from "@/services/ai/ai-generation";
import { useToast } from "@/composables/ui/useToast";
import { useResolvedAiPrompts } from "@/composables/useAiPrompts";

interface Props {
  show: boolean;
  channels: Channel[];
  defaultAccountId?: string;
  isLoading?: boolean;
  autoSetChannelType?: string;
  hideAccountSelection?: boolean;
  contactPhoneNumbers?: Array<{ type: string; number: string }>;
  initialMessage?: string;
  contactId?: string;
  icpId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  initialMessage: "",
});

const emit = defineEmits<{
  close: [];
  "create-chat": [request: CreateChatRequest];
}>();

const $toast = useToast();

// Filter channels to only non-email, connected channels
const availableChannels = computed(() => {
  let filtered = props.channels.filter(
    (channel) =>
      channel.type !== "email" &&
      channel.isConnected &&
      channel.accountId &&
      ["OK", "CREATION_SUCCESS", "RECONNECTED", "SYNC_SUCCESS"].includes(
        channel.status?.toUpperCase() || ""
      )
  );

  if (props.autoSetChannelType) {
    filtered = filtered.filter(
      (channel) => channel.type?.toLowerCase() === props.autoSetChannelType?.toLowerCase()
    );
  }

  return filtered;
});

// Account options for Select
const accountOptions = computed<SelectOption[]>(() => {
  return availableChannels.value.map((channel) => ({
    value: channel.accountId!,
    label: channel.name || "Unknown Account",
    icon: channel.icon || getChannelIcon(channel.type),
  }));
});

// Form data
const formData = ref<CreateChatRequest>({
  account_id: "",
  attendees_ids: [],
  text: "",
});

const attendeesInput = ref("");
const attendeeTags = ref<string[]>([]);
const selectedPhoneNumber = ref<string>("");
const showAiPanel = ref(false);
const showAiComposePanel = ref(false);
const isGeneratingAiCustom = ref(false);
const isGeneratingAiCompose = ref(false);
const aiRefineButtonRef = ref<HTMLElement | null>(null);
const aiComposeButtonRef = ref<HTMLElement | null>(null);
const resolvedAiPromptsQuery = useResolvedAiPrompts();

const toggleAiRefinePanel = () => {
  showAiComposePanel.value = false;
  showAiPanel.value = !showAiPanel.value;
};

const toggleAiComposePanel = () => {
  showAiPanel.value = false;
  showAiComposePanel.value = !showAiComposePanel.value;
};

const handleAiComposeGenerate = async (payload: { customInstructions: string }) => {
  if (isGeneratingAiCompose.value) return;
  isGeneratingAiCompose.value = true;
  try {
    const response = await AIGenerationService.generateCompose({
      contactId: props.contactId || "",
      icpId: props.icpId,
      channel: "whatsapp",
      customInstructions: payload.customInstructions,
      aiPromptCompose: resolvedAiPromptsQuery.composeAiPrompt.value,
    });

    if (response.body) {
      formData.value.text = response.body;
      showAiComposePanel.value = false;
      $toast?.success("Message composed successfully");
    }
  } catch (error: any) {
    console.error("Failed to compose message:", error);
    $toast?.error(error.message || "Failed to compose message");
  } finally {
    isGeneratingAiCompose.value = false;
  }
};

const handleAiCustomizationGenerate = async (payload: { customInstructions: string }) => {
  if (isGeneratingAiCustom.value) return;
  isGeneratingAiCustom.value = true;
  try {
    const response = await AIGenerationService.generateRefine({
      contactId: props.contactId || "",
      icpId: props.icpId,
      channel: "whatsapp",
      currentDraft: formData.value.text || "",
      customInstructions: payload.customInstructions,
    });

    if (response.body) {
      formData.value.text = response.body;
      showAiPanel.value = false;
      $toast?.success("Message refined successfully");
    }
  } catch (error: any) {
    console.error("Failed to refine message:", error);
    $toast?.error(error.message || "Failed to refine message");
  } finally {
    isGeneratingAiCustom.value = false;
  }
};

const errors = ref<{
  account_id?: string;
  attendees_ids?: string;
}>({});

const selectedAccount = computed(() => {
  if (!formData.value.account_id) return null;
  return availableChannels.value.find((channel) => channel.accountId === formData.value.account_id);
});

const recipientsPlaceholder = computed(() => {
  if (selectedAccount.value?.type === "linkedin")
    return "Enter LinkedIn profile IDs (comma-separated)";
  if (selectedAccount.value?.type === "whatsapp")
    return "Enter WhatsApp phone numbers (comma-separated)";
  return "Enter recipient IDs (comma-separated)";
});

const recipientsHelperText = computed(() => {
  if (selectedAccount.value?.type === "linkedin") {
    return "Enter LinkedIn profile IDs (e.g., ACwAAAAAAABD)";
  }
  if (selectedAccount.value?.type === "whatsapp") {
    return "Enter phone numbers in international format (e.g., +14155552671)";
  }
  return "Enter recipient IDs separated by commas";
});

// Check if form can be submitted
const canSubmit = computed(() => {
  return (
    formData.value.account_id &&
    formData.value.attendees_ids.length > 0 &&
    !errors.value.account_id &&
    !errors.value.attendees_ids
  );
});

// Handle attendees input change
const handleAttendeesInput = () => {
  // Parse comma-separated values
  const values = attendeesInput.value
    .split(",")
    .map((v) => v.trim())
    .filter((v) => v.length > 0);

  // Update tags
  attendeeTags.value = values;

  // Update form data
  formData.value.attendees_ids = values;

  // Validate
  if (values.length === 0 && attendeesInput.value.trim().length > 0) {
    errors.value.attendees_ids = "Please enter at least one recipient ID";
  } else {
    errors.value.attendees_ids = undefined;
  }
};

// Remove attendee tag
const removeAttendeeTag = (index: number) => {
  attendeeTags.value.splice(index, 1);
  formData.value.attendees_ids = [...attendeeTags.value];
  attendeesInput.value = attendeeTags.value.join(", ");
};

// Get channel icon
const getChannelIcon = (type: string): string => {
  switch (type?.toLowerCase()) {
    case "linkedin":
      return "mdi:linkedin";
    case "whatsapp":
      return "mdi:whatsapp";
    default:
      return "mdi:message-outline";
  }
};

// Handle form submission
const handleSubmit = () => {
  // Reset errors
  errors.value = {};

  // Validate account
  if (!formData.value.account_id) {
    errors.value.account_id = "Please select an account";
    return;
  }

  // Validate attendees
  if (formData.value.attendees_ids.length === 0) {
    errors.value.attendees_ids = "Please enter at least one recipient ID";
    return;
  }

  // Prepare request
  const request: CreateChatRequest = {
    account_id: formData.value.account_id,
    attendees_ids: formData.value.attendees_ids,
  };

  if (formData.value.text?.trim()) {
    request.text = formData.value.text.trim();
  }

  emit("create-chat", request);
};

// Reset form when modal closes
watch(
  () => props.show,
  (isShowing) => {
    if (!isShowing) {
      // Reset form
      formData.value = {
        account_id: props.defaultAccountId || "",
        attendees_ids: [],
        text: "",
      };
      attendeesInput.value = "";
      attendeeTags.value = [];
      selectedPhoneNumber.value = "";
      showAiPanel.value = false;
      showAiComposePanel.value = false;
      errors.value = {};
    } else {
      if (props.defaultAccountId) {
        formData.value.account_id = props.defaultAccountId;
      }
      if (props.initialMessage) {
        formData.value.text = props.initialMessage;
      }
    }
  }
);

const phoneNumberOptions = computed<SelectOption[]>(() => {
  if (!props.contactPhoneNumbers || props.contactPhoneNumbers.length === 0) {
    return [];
  }
  return props.contactPhoneNumbers.map((phone) => ({
    value: phone.number,
    label: `${phone.type}: ${phone.number}`,
  }));
});

const handlePhoneNumberSelect = (value: string | number) => {
  const phoneNumber = String(value);
  selectedPhoneNumber.value = phoneNumber;
  attendeesInput.value = phoneNumber;
  handleAttendeesInput();
};

watch(
  () => props.defaultAccountId,
  (newAccountId) => {
    if (newAccountId && props.show) {
      formData.value.account_id = newAccountId;
    }
  }
);

// Watch for autoSetChannelType to auto-set account
watch(
  () => [props.autoSetChannelType, props.show, availableChannels.value],
  () => {
    if (props.autoSetChannelType && props.show && availableChannels.value.length > 0) {
      const matchingChannel = availableChannels.value.find(
        (channel) => channel.type?.toLowerCase() === props.autoSetChannelType?.toLowerCase()
      );
      if (matchingChannel?.accountId) {
        formData.value.account_id = matchingChannel.accountId;
      } else if (props.defaultAccountId) {
        formData.value.account_id = props.defaultAccountId;
      }
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.whatsapp-header__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.whatsapp-header__icon-title {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.whatsapp-header__icon-container {
  width: 40px;
  height: 40px;
  background: #25d366;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.whatsapp-header__icon {
  font-size: 24px;
  color: white;
}

.whatsapp-header__title {
  margin: 0 0 4px 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a2e;
  letter-spacing: -0.01em;
}

.whatsapp-header__subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: #646464;
}

.whatsapp-header__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: #646464;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.whatsapp-header__close:hover {
  background: #e8e8f0;
  color: #1a1a2e;
}

.whatsapp-form {
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  max-height: calc(75vh - 130px);
}

.whatsapp-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.whatsapp-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.whatsapp-optional {
  font-size: 0.75rem;
  font-weight: 400;
  color: #6b7280;
}

.whatsapp-textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-size: 0.875rem;
  font-family: inherit;
  resize: vertical;
  outline: none;
  transition: border-color 0.2s;
  min-height: 120px;
}

.whatsapp-textarea:focus {
  border-color: #3e3efe;
}

.whatsapp-textarea::placeholder {
  color: #9ca3af;
}

.whatsapp-attendee-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.whatsapp-attendee-tag {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: #f3f4ff;
  border: 1px solid #e0e7ff;
  border-radius: 6px;
  font-size: 0.8125rem;
  color: #3e3efe;
}

.whatsapp-attendee-tag-text {
  font-weight: 500;
}

.whatsapp-attendee-tag-remove {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #3e3efe;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  font-size: 1rem;
}

.whatsapp-attendee-tag-remove:hover {
  color: #2d2dff;
}

.whatsapp-ai-row {
  display: flex;
  align-items: center;
  margin-top: 4px;
}

.whatsapp-ai-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 24px;
  padding: 4px 10px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.7rem;
  font-weight: 500;
  flex-shrink: 0;
  white-space: nowrap;
  background: rgba(107, 114, 128, 0.12);
  color: #4b5563;
}

.whatsapp-ai-btn:hover:not(:disabled) {
  background: rgba(107, 114, 128, 0.2);
  color: #374151;
}

.whatsapp-ai-btn--compose {
  background: rgba(8, 145, 178, 0.1);
  color: #0891b2;
}

.whatsapp-ai-btn--compose .whatsapp-ai-btn-icon {
  color: #0891b2;
}

.whatsapp-ai-btn--compose:hover:not(:disabled) {
  background: rgba(8, 145, 178, 0.2);
  color: #0e7490;
}

.whatsapp-ai-btn--compose:hover:not(:disabled) .whatsapp-ai-btn-icon {
  color: #0e7490;
}

.whatsapp-ai-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.whatsapp-ai-btn-icon {
  font-size: 0.75rem;
  color: #4b5563;
}

.whatsapp-ai-btn:hover:not(:disabled) .whatsapp-ai-btn-icon {
  color: #374151;
}
</style>
